import React, { useMemo } from 'react';
import { Stack, styled, Typography, Button } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import { ResourceSectionFrame } from './ResourceSectionFrame';

interface ResourceSectionProps {
  title: string;
  children: React.ReactNode;
  titleId?: string;
  description?: string;
  viewAllText?: string;
  viewAllAriaLabel?: string;
  onViewAll?: () => void;
  showViewAll?: boolean;
  headerAction?: React.ReactNode;
}

const HeaderSection = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const MobileButtonSection = styled('div')(() => ({
  textAlign: 'center',
}));

const ViewAllButton = styled(Button)(() => ({
  fontWeight: 500,
  textTransform: 'none',
  fontSize: '16px',
  lineHeight: '21px',
}));

export const ResourceSection: React.FC<ResourceSectionProps> = ({
  title,
  children,
  titleId,
  description,
  viewAllText = 'View all',
  viewAllAriaLabel,
  onViewAll,
  headerAction,
  showViewAll = true,
}) => {
  const { isMobile, isTablet } = useDeviceType();

  const getTitleVariant = useMemo(() => {
    if (isMobile) return 'callout';
    if (isTablet) return 'headline';
    return 'title3';
  }, [isMobile, isTablet]);

  const descriptionId = titleId ? `${titleId}-description` : undefined;

  const viewAllLink = (
    <ViewAllButton
      variant="text"
      endIcon={<ChevronRight />}
      color="primary"
      onClick={onViewAll}
      aria-label={viewAllAriaLabel || `${viewAllText} for ${title}`}
    >
      {viewAllText}
    </ViewAllButton>
  );

  return (
    <ResourceSectionFrame role="region" aria-labelledby={titleId} aria-describedby={descriptionId}>
      <HeaderSection>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="column" spacing={1}>
            <Typography fontWeight={500} color="grey.800" variant={getTitleVariant} id={titleId}>
              {title}
            </Typography>
            <Typography variant="callout" color="grey.700" id={descriptionId}>
              {description}
            </Typography>
          </Stack>
          {!isMobile && (headerAction ?? (showViewAll ? viewAllLink : null))}
        </Stack>
      </HeaderSection>
      {children}
      {isMobile && !headerAction && showViewAll && (
        <MobileButtonSection>{viewAllLink}</MobileButtonSection>
      )}
    </ResourceSectionFrame>
  );
};
